import { useMemo } from 'react';
import { Checkbox, createStyles } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';

import type { CellComponentProps, CellDescriptor, RefineValue } from '../../types/common';
import useAlignStyle from '../../../TableModal/useAlignStyle';

import type { TableCellDataCheckboxType, TableColumnCheckboxType } from './types';

const useStyles = createStyles(() => ({
  cellWrapper: {
    display: 'flex',
  },
  checkboxLabel: {
    whiteSpace: 'nowrap',
  },
}));

function TableCellCheckbox({
  column,
  cell,
  value,
  allValues,
  onChange,
}: CellComponentProps<TableColumnCheckboxType, TableCellDataCheckboxType>) {
  const { classes, cx } = useStyles();
  const { classes: alignClasses } = useAlignStyle();

  const isDisabled = useMemo(() => {
    if (!allValues) {
      return false;
    }

    const allTrueValues = Object.values(allValues)
      .map((rowValues) => rowValues[column.key])
      .filter(Boolean);

    if (column.validation?.max && !value) {
      return allTrueValues.length >= column.validation.max;
    }

    return false;
  }, [allValues, column.key, column.validation.max, value]);

  return (
    <div
      className={cx(classes.cellWrapper, {
        [alignClasses.start]: column.params?.align === 'start',
        [alignClasses.center]: column.params?.align === 'center',
        [alignClasses.end]: column.params?.align === 'end',
      })}
    >
      <Checkbox
        name={column.key}
        label={cell.label}
        checked={typeof value === 'boolean' ? value : false}
        disabled={isDisabled}
        labelPosition={column.params?.align === 'start' ? 'left' : 'right'}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
        classNames={{
          label: classes.checkboxLabel,
        }}
      />
    </div>
  );
}

const descriptor: CellDescriptor<TableColumnCheckboxType, TableCellDataCheckboxType> = {
  name: 'Checkbox',
  Cell: TableCellCheckbox,
  createValidationSchema(column) {
    let schema: ZodTypeAny = z.boolean();
    const refines: RefineValue[] = [];

    if (column.validation?.max) {
      refines.push([
        (input) => {
          if (!input) {
            return true;
          }

          const allTrueValues = Object.values(input)
            .map((rowValues) => rowValues[column.key])
            .filter(Boolean);
          return allTrueValues.length <= column.validation.max;
        },
        `You can only select maximum of ${column.validation.max} options`,
      ]);
    }
    if (column.validation?.min) {
      refines.push([
        (input) => {
          if (!input) {
            return false;
          }

          const allTrueValues = Object.values(input)
            .map((rowValues) => rowValues[column.key])
            .filter(Boolean);

          return allTrueValues.length >= column.validation.min;
        },
        `You can only select minimum of ${column.validation.min} options`,
      ]);
    }

    schema = schema.default(false);

    return {
      schema,
      refines,
    };
  },
};

export default descriptor;
